import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash, Target, Sparkle, CalendarPlus, Spinner, CheckCircle, Trophy, Lightning, Timer, Barbell, Coffee, Info, CaretDown } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useWeightUnit } from "@/lib/units";
import { PRIMARY_GOALS, findGoal } from "@/lib/goals";

/**
 * Helper: AI-generated weight_guidance strings look like "60-70 kg", "20 kg DB each",
 * "Bodyweight", "RPE 7". When the user is in lb mode, we keep the AI's original kg
 * recommendation and append a lb hint so they don't have to mentally convert.
 */
function annotateWeightGuidance(text, weightUnit, fromKg) {
  if (!text || weightUnit !== "lb") return text;
  return text.replace(/(\d+(?:\.\d+)?)(?:\s*[-–]\s*(\d+(?:\.\d+)?))?\s*kg\b/gi, (_, a, b) => {
    const lo = fromKg(Number(a));
    const hi = b ? fromKg(Number(b)) : null;
    const fmt = (n) => Number(n.toFixed(1));
    return hi != null
      ? `${a}-${b} kg (≈ ${fmt(lo)}-${fmt(hi)} lb)`
      : `${a} kg (≈ ${fmt(lo)} lb)`;
  });
}

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const GOAL_TYPES = [
  { value: "weight_target", label: "Weight Target", unit: "kg" },
  { value: "body_fat", label: "Body Fat %", unit: "%" },
  { value: "workout_count", label: "Workout Sessions", unit: "sessions" },
  { value: "volume", label: "Total Volume", unit: "kg" },
  { value: "custom", label: "Custom Goal", unit: "" },
];

const SECTIONS = [
  { id: "programs", label: "Training Programs", icon: CalendarPlus },
  { id: "goals", label: "Goals", icon: Target },
  { id: "ai", label: "AI Plan Generator", icon: Sparkle },
];

export default function Plans({ api, templates, profile, onScheduleRefresh, onTemplatesRefresh }) {
  const [section, setSection] = useState("programs");
  const [programs, setPrograms] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    try {
      const [pRes, gRes] = await Promise.all([
        axios.get(`${api}/programs`),
        axios.get(`${api}/goals`),
      ]);
      setPrograms(pRes.data || []);
      setGoals(gRes.data || []);
    } catch (e) {
      console.error("plans fetch", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [api]);

  return (
    <div className="space-y-6" data-testid="plans-page">
      <div>
        <h2
          className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-white"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          data-testid="plans-title"
        >
          Plans
        </h2>
        <p className="text-sm text-[#71717A] mt-1">Build training programs, set goals, and let AI design a routine</p>
      </div>

      {/* Section tabs */}
      <div className="flex items-center gap-2 border-b border-[#27272A]" data-testid="plans-section-tabs">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            onClick={() => setSection(s.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-all border-b-2 -mb-px ${
              section === s.id
                ? "border-[#E05A1D] text-[#E05A1D]"
                : "border-transparent text-[#A1A1AA] hover:text-white"
            }`}
            data-testid={`plans-tab-${s.id}`}
          >
            <s.icon size={14} weight={section === s.id ? "fill" : "regular"} />
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20" data-testid="plans-loading">
          <Spinner size={24} className="animate-spin text-[#E05A1D]" />
        </div>
      ) : section === "programs" ? (
        <ProgramsSection
          api={api}
          templates={templates}
          programs={programs}
          onRefresh={fetchAll}
          onScheduleRefresh={onScheduleRefresh}
          onTemplatesRefresh={onTemplatesRefresh}
        />
      ) : section === "goals" ? (
        <GoalsSection api={api} goals={goals} onRefresh={fetchAll} />
      ) : (
        <AIGeneratorSection api={api} profile={profile} onRefresh={fetchAll} onSwitchToPrograms={() => setSection("programs")} onTemplatesRefresh={onTemplatesRefresh} />
      )}
    </div>
  );
}

/* ────────────────────────── Programs ────────────────────────── */

function ProgramsSection({ api, templates, programs, onRefresh, onScheduleRefresh, onTemplatesRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [weeks, setWeeks] = useState(4);
  const [goalFocus, setGoalFocus] = useState("Strength");
  const [days, setDays] = useState([]); // {day_index, template_id, workout_name, rest}
  const [saving, setSaving] = useState(false);
  const [applyTarget, setApplyTarget] = useState(null);
  const [applyDate, setApplyDate] = useState(new Date().toISOString().slice(0, 10));
  const [applying, setApplying] = useState(false);
  const [detailDay, setDetailDay] = useState(null); // { day, programName }

  const sortedTemplates = [...(templates || [])].sort((a, b) => {
    const ap = a.is_preset ? 1 : 0;
    const bp = b.is_preset ? 1 : 0;
    if (ap !== bp) return ap - bp;
    return (a.name || "").localeCompare(b.name || "");
  });

  const addDay = () => setDays([...days, { day_index: days.length % 7, template_id: null, workout_name: "", rest: false }]);
  const removeDay = (idx) => setDays(days.filter((_, i) => i !== idx));
  const updateDay = (idx, field, value) => {
    const next = [...days];
    next[idx] = { ...next[idx], [field]: value };
    setDays(next);
  };
  const onTemplatePick = (idx, tplId) => {
    const t = sortedTemplates.find((x) => x.id === tplId);
    updateDay(idx, "template_id", tplId);
    if (t && !days[idx].workout_name) updateDay(idx, "workout_name", t.name);
  };

  const resetForm = () => {
    setName(""); setDescription(""); setWeeks(4); setGoalFocus("Strength"); setDays([]);
  };

  const saveProgram = async () => {
    if (!name.trim()) { toast.error("Program name required"); return; }
    if (days.length === 0) { toast.error("Add at least one day"); return; }
    setSaving(true);
    try {
      await axios.post(`${api}/programs`, {
        name: name.trim(),
        description,
        weeks_count: Math.max(1, parseInt(weeks) || 1),
        goal_focus: goalFocus,
        days: days.map((d) => ({
          day_index: parseInt(d.day_index) || 0,
          template_id: d.template_id || null,
          workout_name: d.workout_name || (d.rest ? "Rest" : "Workout"),
          rest: !!d.rest,
        })),
      });
      toast.success("Program created!");
      setShowForm(false);
      resetForm();
      onRefresh();
    } catch (e) {
      toast.error("Failed to create program");
    } finally {
      setSaving(false);
    }
  };

  const deleteProgram = async (id) => {
    try {
      const res = await axios.delete(`${api}/programs/${id}`);
      const removed = res?.data?.schedule_entries_removed || 0;
      toast.success(removed > 0 ? `Program deleted · ${removed} scheduled session${removed === 1 ? "" : "s"} removed` : "Program deleted");
      await fetchAll();
      onScheduleRefresh?.();
      onTemplatesRefresh?.();
    } catch {
      toast.error("Delete failed");
    }
  };

  const applyProgram = async () => {
    if (!applyTarget) return;
    setApplying(true);
    try {
      const res = await axios.post(`${api}/programs/${applyTarget.id}/apply`, { start_date: applyDate });
      toast.success(`Scheduled ${res.data.scheduled} sessions`);
      setApplyTarget(null);
      onScheduleRefresh?.();
    } catch (e) {
      toast.error("Failed to apply program");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#71717A] uppercase tracking-wider">Structured multi-week training programs</p>
        <Dialog open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <button
              className="bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide px-4 py-2 text-xs flex items-center gap-1.5 transition-colors"
              data-testid="new-program-btn"
            >
              <Plus size={14} weight="bold" /> New Program
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#161616] border-[#27272A] max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold uppercase tracking-tight text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                Create Program
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Name</label>
                  <input
                    type="text" value={name} onChange={(e) => setName(e.target.value)}
                    className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
                    placeholder="e.g. PPL — 4 Week Block"
                    data-testid="program-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Weeks</label>
                  <input
                    type="number" min="1" max="52" value={weeks} onChange={(e) => setWeeks(e.target.value)}
                    className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
                    data-testid="program-weeks-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Focus</label>
                  <select value={goalFocus} onChange={(e) => setGoalFocus(e.target.value)}
                    className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
                    data-testid="program-focus-select">
                    <option value="Strength">Strength</option>
                    <option value="Hypertrophy">Hypertrophy / Mass</option>
                    <option value="Fat Loss">Fat Loss</option>
                    <option value="Endurance">Endurance</option>
                    <option value="General Fitness">General Fitness</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Description</label>
                  <input type="text" value={description} onChange={(e) => setDescription(e.target.value)}
                    className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
                    placeholder="Brief description" data-testid="program-description-input" />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A]">Weekly Structure</label>
                  <button onClick={addDay} className="text-xs text-[#E05A1D] font-bold uppercase flex items-center gap-1" data-testid="add-program-day-btn">
                    <Plus size={14} weight="bold" /> Add Day
                  </button>
                </div>
                <div className="space-y-2">
                  {days.map((d, i) => (
                    <div key={i} className="border border-[#27272A] bg-[#0F0F0F] p-2.5 space-y-2" data-testid={`program-day-${i}`}>
                      <div className="grid grid-cols-[110px_1fr_auto] gap-2 items-center">
                        <select value={d.day_index} onChange={(e) => updateDay(i, "day_index", parseInt(e.target.value))}
                          className="bg-[#161616] border border-[#27272A] text-white px-2 py-1.5 text-sm focus:border-[#E05A1D] outline-none"
                          data-testid={`program-day-index-${i}`}>
                          {DAY_NAMES.map((dn, idx) => (
                            <option key={idx} value={idx}>{dn}</option>
                          ))}
                        </select>
                        <input
                          type="text"
                          value={d.workout_name}
                          onChange={(e) => updateDay(i, "workout_name", e.target.value)}
                          placeholder={d.rest ? "Rest day" : "Workout name"}
                          className="bg-[#161616] border border-[#27272A] text-white px-3 py-1.5 text-sm focus:border-[#E05A1D] outline-none"
                          disabled={d.rest}
                          data-testid={`program-day-name-${i}`}
                        />
                        <button onClick={() => removeDay(i)} className="text-[#71717A] hover:text-red-500 transition-colors" data-testid={`program-day-remove-${i}`}>
                          <Trash size={16} />
                        </button>
                      </div>
                      <div className="grid grid-cols-[110px_1fr] gap-2 items-center">
                        <label className="flex items-center gap-1.5 text-xs text-[#A1A1AA]">
                          <input
                            type="checkbox"
                            checked={d.rest}
                            onChange={(e) => updateDay(i, "rest", e.target.checked)}
                            data-testid={`program-day-rest-${i}`}
                          />
                          Rest day
                        </label>
                        {!d.rest && (
                          <Select value={d.template_id || ""} onValueChange={(v) => onTemplatePick(i, v)}>
                            <SelectTrigger className="bg-[#161616] border-[#27272A] text-white h-8 text-sm" data-testid={`program-day-template-${i}`}>
                              <SelectValue placeholder="Link a workout (optional)" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#1C1C1C] border-[#27272A]">
                              {sortedTemplates.some((t) => !t.is_preset) && (
                                <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-[#E05A1D]">Your Workouts</div>
                              )}
                              {sortedTemplates.filter((t) => !t.is_preset).map((t) => (
                                <SelectItem key={t.id} value={t.id} className="text-white hover:bg-[#262626]">{t.name}</SelectItem>
                              ))}
                              {sortedTemplates.some((t) => !t.is_preset) && sortedTemplates.some((t) => t.is_preset) && (
                                <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-[#71717A] border-t border-[#27272A]">Workouts</div>
                              )}
                              {sortedTemplates.filter((t) => t.is_preset).map((t) => (
                                <SelectItem key={t.id} value={t.id} className="text-white hover:bg-[#262626]">{t.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>
                  ))}
                  {days.length === 0 && (
                    <p className="text-xs text-[#71717A] py-4 text-center border border-dashed border-[#27272A]">
                      Add training and rest days for one week. The pattern will repeat for {weeks} week{weeks > 1 ? "s" : ""}.
                    </p>
                  )}
                </div>
              </div>

              <button
                onClick={saveProgram}
                disabled={saving}
                className="w-full bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide py-3 transition-colors disabled:opacity-50"
                data-testid="save-program-btn"
              >
                {saving ? "Saving..." : "Save Program"}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {programs.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-[#27272A]" data-testid="no-programs">
          <CalendarPlus size={40} className="text-[#27272A] mx-auto mb-3" />
          <p className="text-sm text-[#71717A]">No programs yet. Build one above or generate via AI.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {programs.map((p) => (
            <div key={p.id} className="bg-[#161616] border border-[#27272A] p-5 space-y-3 hover:border-[#3f3f46] transition-colors" data-testid={`program-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {p.is_ai_generated && <Sparkle size={14} weight="fill" className="text-[#E05A1D]" />}
                    <h4 className="font-semibold text-white truncate">{p.name}</h4>
                    {p.is_cycle && (
                      <span
                        className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border border-[#8B5CF6]/40 text-[#8B5CF6] bg-[#8B5CF6]/10"
                        data-testid={`program-cycle-badge-${p.id}`}
                      >
                        Cycle
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] text-[#E05A1D] font-bold uppercase">{p.goal_focus || "General"}</span>
                    <span className="text-[10px] text-[#A1A1AA]">{p.weeks_count}w · {(p.days || []).filter((d) => !d.rest).length} workouts/wk</span>
                  </div>
                </div>
                <button onClick={() => deleteProgram(p.id)} className="text-[#71717A] hover:text-red-500 transition-colors" data-testid={`program-delete-${p.id}`}>
                  <Trash size={14} />
                </button>
              </div>
              {p.description && (
                <p className="text-xs text-[#A1A1AA] line-clamp-2">{p.description}</p>
              )}
              {p.is_cycle && p.phases?.length > 0 && <PhaseTimeline phases={p.phases} weeks={p.weeks_count} testId={`program-phases-${p.id}`} />}
              {(p.days || []).some((d) => d.exercises?.length > 0) && (
                <p className="text-[9px] uppercase tracking-wider text-[#71717A]">Tap a day for details</p>
              )}
              <ProgramWeeksOverview
                program={p}
                dense
                testIdPrefix={`program-${p.id}-overview`}
                onDayClick={(d) => setDetailDay({ day: d, programName: p.name, programId: p.id, program: p })}
              />
              <button
                onClick={() => { setApplyTarget(p); setApplyDate(new Date().toISOString().slice(0, 10)); }}
                className="w-full bg-[#E05A1D]/10 hover:bg-[#E05A1D] text-[#E05A1D] hover:text-white border border-[#E05A1D]/40 font-bold uppercase tracking-wide py-2 text-xs flex items-center justify-center gap-1.5 transition-all"
                data-testid={`program-apply-${p.id}`}
              >
                <CalendarPlus size={14} weight="bold" /> Apply to Schedule
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Apply-to-schedule dialog */}
      <Dialog open={!!applyTarget} onOpenChange={(o) => { if (!o) setApplyTarget(null); }}>
        <DialogContent className="bg-[#161616] border-[#27272A] max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold uppercase tracking-tight text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Apply "{applyTarget?.name}"
            </DialogTitle>
            <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">
              Review the program before scheduling
            </p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {applyTarget && (
              <>
                {/* Program meta */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-[#0F0F0F] border border-[#27272A] p-2 text-center">
                    <p className="text-[9px] font-bold tracking-[0.1em] uppercase text-[#71717A]">Duration</p>
                    <p className="text-base font-bold text-white">{applyTarget.weeks_count} week{applyTarget.weeks_count > 1 ? "s" : ""}</p>
                  </div>
                  <div className="bg-[#0F0F0F] border border-[#27272A] p-2 text-center">
                    <p className="text-[9px] font-bold tracking-[0.1em] uppercase text-[#71717A]">Focus</p>
                    <p className="text-base font-bold text-[#E05A1D]">{applyTarget.goal_focus || "—"}</p>
                  </div>
                  <div className="bg-[#0F0F0F] border border-[#27272A] p-2 text-center">
                    <p className="text-[9px] font-bold tracking-[0.1em] uppercase text-[#71717A]">Workouts</p>
                    <p className="text-base font-bold text-white">
                      {(() => {
                        const perWeek = (applyTarget.days || []).filter((d) => !d.rest).length;
                        return perWeek * applyTarget.weeks_count;
                      })()}
                    </p>
                  </div>
                </div>

                {/* Per-week overview */}
                <div>
                  <p className="text-[10px] font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1.5">
                    All weeks · tap any day for details
                  </p>
                  <ProgramWeeksOverview
                    program={applyTarget}
                    testIdPrefix="apply-overview"
                    onDayClick={(d, wi) => setDetailDay({ day: d, programName: applyTarget.name, programId: applyTarget.id, program: applyTarget, weekIdx: wi })}
                  />
                </div>

                <div className="border-t border-[#27272A] pt-3">
                  <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Start date</label>
                  <input
                    type="date" value={applyDate} onChange={(e) => setApplyDate(e.target.value)}
                    className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
                    data-testid="program-apply-date"
                  />
                  <p className="text-[10px] text-[#71717A] mt-1.5">
                    The weekly pattern will be repeated for {applyTarget?.weeks_count || 0} weeks, starting from this date's Monday.
                  </p>
                </div>
                <button
                  onClick={applyProgram}
                  disabled={applying}
                  className="w-full bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide py-3 transition-colors disabled:opacity-50"
                  data-testid="program-apply-confirm"
                >
                  {applying ? "Scheduling..." : "Add to Schedule"}
                </button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Day detail dialog */}
      <DayDetailDialog
        open={!!detailDay}
        onClose={() => setDetailDay(null)}
        day={detailDay?.day}
        programName={detailDay?.programName}
        programId={detailDay?.programId}
        program={detailDay?.program}
        linkedTemplate={detailDay?.day?.template_id ? (templates || []).find((t) => t.id === detailDay.day.template_id) : null}
        api={api}
        onTemplatesRefresh={onTemplatesRefresh}
        onProgramsRefresh={onRefresh}
      />
    </div>
  );
}

/* ─────────────────────────── Goals ──────────────────────────── */

function GoalsSection({ api, goals, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [type, setType] = useState("weight_target");
  const [title, setTitle] = useState("");
  const [targetValue, setTargetValue] = useState("");
  const [startValue, setStartValue] = useState("");
  const [deadline, setDeadline] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const currentUnit = GOAL_TYPES.find((g) => g.value === type)?.unit || "";

  const resetForm = () => {
    setType("weight_target"); setTitle(""); setTargetValue(""); setStartValue(""); setDeadline(""); setNotes("");
  };

  const saveGoal = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    if (!targetValue) { toast.error("Target value required"); return; }
    setSaving(true);
    try {
      await axios.post(`${api}/goals`, {
        type,
        title: title.trim(),
        target_value: parseFloat(targetValue) || 0,
        unit: currentUnit,
        start_value: parseFloat(startValue) || 0,
        current_value: parseFloat(startValue) || 0,
        deadline: deadline || null,
        notes,
      });
      toast.success("Goal created!");
      setShowForm(false);
      resetForm();
      onRefresh();
    } catch {
      toast.error("Failed to create goal");
    } finally {
      setSaving(false);
    }
  };

  const updateGoal = async (id, payload) => {
    try { await axios.put(`${api}/goals/${id}`, payload); onRefresh(); }
    catch { toast.error("Update failed"); }
  };

  const deleteGoal = async (id) => {
    try { await axios.delete(`${api}/goals/${id}`); toast.success("Goal deleted"); onRefresh(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#71717A] uppercase tracking-wider">Set targets and track progress</p>
        <Dialog open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <button
              className="bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide px-4 py-2 text-xs flex items-center gap-1.5 transition-colors"
              data-testid="new-goal-btn"
            >
              <Plus size={14} weight="bold" /> New Goal
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#161616] border-[#27272A] max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold uppercase tracking-tight text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                Create Goal
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Goal type</label>
                <select value={type} onChange={(e) => setType(e.target.value)}
                  className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
                  data-testid="goal-type-select">
                  {GOAL_TYPES.map((g) => (<option key={g.value} value={g.value}>{g.label}</option>))}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Title</label>
                <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                  className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
                  placeholder="e.g. Hit 80kg bench press" data-testid="goal-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Start value {currentUnit && `(${currentUnit})`}</label>
                  <input type="number" step="0.1" value={startValue} onChange={(e) => setStartValue(e.target.value)}
                    className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
                    data-testid="goal-start-input" />
                </div>
                <div>
                  <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Target value {currentUnit && `(${currentUnit})`}</label>
                  <input type="number" step="0.1" value={targetValue} onChange={(e) => setTargetValue(e.target.value)}
                    className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
                    data-testid="goal-target-input" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Deadline (optional)</label>
                <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)}
                  className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
                  data-testid="goal-deadline-input" />
              </div>
              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Notes</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                  className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm h-16 resize-none focus:border-[#E05A1D] outline-none"
                  data-testid="goal-notes-input" />
              </div>
              <button onClick={saveGoal} disabled={saving}
                className="w-full bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide py-3 transition-colors disabled:opacity-50"
                data-testid="save-goal-btn">
                {saving ? "Saving..." : "Save Goal"}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {goals.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-[#27272A]" data-testid="no-goals">
          <Target size={40} className="text-[#27272A] mx-auto mb-3" />
          <p className="text-sm text-[#71717A]">No goals yet. Set your first target above.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {goals.map((g) => <GoalCard key={g.id} goal={g} onUpdate={updateGoal} onDelete={deleteGoal} />)}
        </div>
      )}
    </div>
  );
}

function GoalCard({ goal, onUpdate, onDelete }) {
  const [current, setCurrent] = useState(goal.current_value);

  useEffect(() => { setCurrent(goal.current_value); }, [goal.current_value]);

  // Progress (handle decreasing targets too — e.g. body fat down)
  const start = goal.start_value || 0;
  const target = goal.target_value || 0;
  const cur = current || 0;
  let progressPct = 0;
  if (target === start) {
    progressPct = cur >= target ? 100 : 0;
  } else {
    progressPct = ((cur - start) / (target - start)) * 100;
  }
  progressPct = Math.max(0, Math.min(100, progressPct));
  const isComplete = goal.completed || progressPct >= 100;

  return (
    <div className={`border p-5 space-y-3 transition-colors ${
      isComplete ? "bg-[#22C55E]/5 border-[#22C55E]/30" : "bg-[#161616] border-[#27272A]"
    }`} data-testid={`goal-card-${goal.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {isComplete ? <Trophy size={16} weight="fill" className="text-[#22C55E]" /> : <Target size={16} weight="fill" className="text-[#E05A1D]" />}
            <h4 className={`font-semibold truncate ${isComplete ? "text-[#22C55E]" : "text-white"}`}>{goal.title}</h4>
          </div>
          <p className="text-[10px] uppercase tracking-wider text-[#71717A] mt-0.5">
            {GOAL_TYPES.find((t) => t.value === goal.type)?.label || goal.type}
            {goal.deadline && <> · Due {goal.deadline}</>}
          </p>
        </div>
        <button onClick={() => onDelete(goal.id)} className="text-[#71717A] hover:text-red-500 transition-colors" data-testid={`goal-delete-${goal.id}`}>
          <Trash size={14} />
        </button>
      </div>

      <div className="space-y-1.5">
        <div className="flex items-baseline justify-between">
          <span className="text-2xl font-black text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }} data-testid={`goal-current-display-${goal.id}`}>
            {cur}{goal.unit && <span className="text-xs text-[#A1A1AA] ml-1">{goal.unit}</span>}
          </span>
          <span className="text-[10px] uppercase text-[#71717A]">
            {start}{goal.unit} → {target}{goal.unit}
          </span>
        </div>
        <div className="h-1.5 bg-[#0F0F0F] border border-[#27272A] overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${isComplete ? "bg-[#22C55E]" : "bg-[#E05A1D]"}`}
            style={{ width: `${progressPct}%` }}
            data-testid={`goal-progress-${goal.id}`}
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="number"
          step="0.1"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          className="flex-1 bg-[#0F0F0F] border border-[#27272A] text-white px-2 py-1.5 text-sm focus:border-[#E05A1D] outline-none"
          placeholder="Update progress"
          data-testid={`goal-update-input-${goal.id}`}
        />
        <button
          onClick={() => onUpdate(goal.id, { current_value: parseFloat(current) || 0 })}
          className="bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide px-3 py-1.5 text-xs transition-colors"
          data-testid={`goal-update-btn-${goal.id}`}
        >
          Update
        </button>
        {!isComplete && (
          <button
            onClick={() => onUpdate(goal.id, { completed: true })}
            className="bg-transparent border border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E] hover:text-white font-bold uppercase tracking-wide px-2 py-1.5 text-xs transition-all"
            title="Mark as complete"
            data-testid={`goal-complete-btn-${goal.id}`}
          >
            <CheckCircle size={14} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────── AI Generator ───────────────────────── */

function AIGeneratorSection({ api, profile, onRefresh, onSwitchToPrograms, onTemplatesRefresh }) {
  const defaultFocus = findGoal(profile?.primary_goal)?.aiFocus || "Hypertrophy";
  const [focus, setFocus] = useState(defaultFocus);
  const [weeks, setWeeks] = useState(4);
  const [daysPerWeek, setDaysPerWeek] = useState(4);
  const [equipment, setEquipment] = useState("Gym");
  const [notes, setNotes] = useState("");
  const [cycleMode, setCycleMode] = useState(false);
  const [experience, setExperience] = useState("intermediate");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [detailDay, setDetailDay] = useState(null);

  const generate = async () => {
    setError("");
    setResult(null);
    setGenerating(true);
    try {
      const res = await axios.post(`${api}/plans/ai-generate`, {
        goal_focus: focus,
        weeks_count: parseInt(weeks) || 4,
        days_per_week: parseInt(daysPerWeek) || 4,
        equipment,
        notes,
        cycle_mode: cycleMode,
        experience,
      });
      setResult(res.data);
      onRefresh();
      toast.success(cycleMode ? "Cycle generated and saved!" : "Plan generated and saved!");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to generate plan";
      setError(msg);
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-gradient-to-br from-[#E05A1D]/10 to-transparent border border-[#E05A1D]/30 p-5 ai-glow space-y-4" data-testid="ai-plan-card">
        <div className="flex items-center gap-2">
          <Sparkle size={20} weight="fill" className="text-[#E05A1D]" />
          <h3 className="text-base font-bold uppercase tracking-tight text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Let UpTop's coach build your plan
          </h3>
        </div>
        <p className="text-xs text-[#A1A1AA]">GPT-5.2 will design a structured program tailored to your profile, focus, and available equipment.</p>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Focus</label>
            <select value={focus} onChange={(e) => setFocus(e.target.value)}
              className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
              data-testid="ai-focus-select">
              <option value="Strength">Strength</option>
              <option value="Hypertrophy">Hypertrophy / Mass</option>
              <option value="Fat Loss">Fat Loss</option>
              <option value="Endurance">Endurance</option>
              <option value="General Fitness">General Fitness</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Equipment</label>
            <select value={equipment} onChange={(e) => setEquipment(e.target.value)}
              className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
              data-testid="ai-equipment-select">
              <option value="Gym">Gym</option>
              <option value="Home">Home</option>
              <option value="Bodyweight">Bodyweight</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Weeks</label>
            <input type="number" min="1" max="16" value={weeks} onChange={(e) => setWeeks(e.target.value)}
              className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
              data-testid="ai-weeks-input" />
          </div>
          <div>
            <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Days / week</label>
            <input type="number" min="1" max="7" value={daysPerWeek} onChange={(e) => setDaysPerWeek(e.target.value)}
              className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
              data-testid="ai-days-input" />
          </div>
        </div>
        <div>
          <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Notes (optional)</label>
          <input
            type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
            className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
            placeholder="e.g. avoid deadlifts, focus on upper body" data-testid="ai-notes-input"
          />
        </div>

        {/* Cycle mode */}
        <div className="border border-[#27272A] bg-[#0F0F0F] p-3 space-y-3" data-testid="cycle-mode-card">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={cycleMode}
              onChange={(e) => {
                setCycleMode(e.target.checked);
                if (e.target.checked && weeks < 8) setWeeks(12);
              }}
              className="mt-0.5 w-4 h-4 accent-[#E05A1D]"
              data-testid="cycle-mode-toggle"
            />
            <div>
              <p className="text-sm font-bold uppercase tracking-wide text-white">Periodized cycle</p>
              <p className="text-[11px] text-[#A1A1AA] leading-relaxed">
                Split into named blocks (Hypertrophy → Strength → Peak → Deload) with rising intensity. Best for 8-16 weeks.
              </p>
            </div>
          </label>
          {cycleMode && (
            <div data-testid="cycle-experience-row">
              <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Experience</label>
              <select
                value={experience}
                onChange={(e) => setExperience(e.target.value)}
                className="w-full bg-[#161616] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
                data-testid="cycle-experience-select"
              >
                <option value="beginner">Beginner (&lt;1 yr training)</option>
                <option value="intermediate">Intermediate (1-3 yr)</option>
                <option value="advanced">Advanced (3+ yr)</option>
              </select>
            </div>
          )}
        </div>

        <button
          onClick={generate}
          disabled={generating}
          className="w-full bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide py-3 flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
          data-testid="ai-generate-btn"
        >
          {generating ? <><Spinner size={16} className="animate-spin" /> Generating…</> : <><Lightning size={16} weight="fill" /> {cycleMode ? "Generate Cycle" : "Generate Plan"}</>}
        </button>
        {error && <p className="text-xs text-red-500" data-testid="ai-error">{error}</p>}
      </div>

      {result && (
        <div className="bg-[#161616] border border-[#27272A] p-5 space-y-3" data-testid="ai-result">
          <div className="flex items-center gap-2">
            <Sparkle size={16} weight="fill" className="text-[#E05A1D]" />
            <h4 className="text-base font-bold text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{result.name}</h4>
          </div>
          <p className="text-xs text-[#A1A1AA]">{result.description}</p>
          <p className="text-[10px] uppercase tracking-wider text-[#71717A]">Tap any day to see exercises, sets, reps and weight</p>
          <ProgramWeeksOverview
            program={result}
            testIdPrefix="ai-overview"
            onDayClick={(d, wi) => setDetailDay(d)}
          />
          <button onClick={onSwitchToPrograms} className="text-xs text-[#E05A1D] font-bold uppercase tracking-wider hover:text-[#F06B2E] transition-colors" data-testid="ai-view-program-btn">
            View in Programs →
          </button>
        </div>
      )}

      <DayDetailDialog
        open={!!detailDay}
        onClose={() => setDetailDay(null)}
        day={detailDay}
        programName={result?.name}
        programId={result?.id}
        program={result}
        api={api}
        onTemplatesRefresh={onTemplatesRefresh}
        onProgramsRefresh={onRefresh}
        onDayLinked={(dayIndex, tplId) => {
          setResult((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              days: (prev.days || []).map((d) =>
                d.day_index === dayIndex ? { ...d, template_id: tplId } : d
              ),
            };
          });
        }}
      />
    </div>
  );
}

/* ───────────────────────── Day Detail Dialog ─────────────────────────── */

function DayDetailDialog({ open, onClose, day, programName, programId, program, api, onTemplatesRefresh, onProgramsRefresh, onDayLinked, linkedTemplate }) {
  const { unit: weightUnit, fromKg } = useWeightUnit();
  const [saving, setSaving] = useState(false);
  // Week selector — use program.weeks if present, else just the single day
  const weeks = (program?.weeks && program.weeks.length > 0) ? program.weeks : null;
  const dayIndex = day?.day_index;
  const initialWeekIdx = weeks ? Math.max(0, weeks.findIndex((w) =>
    (w.days || []).some((d) => d.day_index === dayIndex && d.workout_name === day?.workout_name)
  )) : 0;
  const [weekIdx, setWeekIdx] = useState(initialWeekIdx);

  // Reset week selection when the dialog is opened on a new day
  useEffect(() => {
    if (open) setWeekIdx(initialWeekIdx >= 0 ? initialWeekIdx : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, day?.day_index, day?.workout_name]);

  // Active day = the day from the chosen week (or the passed-in day if no weeks)
  const activeDay = weeks
    ? ((weeks[weekIdx]?.days || []).find((d) => d.day_index === dayIndex) || day)
    : day;

  if (!activeDay) return null;
  const isRest = !!activeDay.rest;
  const exercises = activeDay.exercises || [];
  const dayName = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"][activeDay.day_index] || "Day";
  const alreadyLinked = !!activeDay.template_id;
  const canSaveAsTemplate = !isRest && exercises.length > 0 && !!api && !alreadyLinked;

  const saveAsTemplate = async () => {
    if (!canSaveAsTemplate) return;
    setSaving(true);
    try {
      const tplExercises = exercises.map((ex) => {
        const repsMatch = String(ex.reps || "").match(/\d+/);
        const repsInt = repsMatch ? parseInt(repsMatch[0]) : 0;
        const setsCount = Math.max(1, parseInt(ex.sets) || 1);
        const notesParts = [];
        if (ex.weight_guidance) notesParts.push(ex.weight_guidance);
        if (ex.reps && repsMatch && repsMatch[0] !== ex.reps) notesParts.push(`reps: ${ex.reps}`);
        if (ex.notes) notesParts.push(ex.notes);
        return {
          name: ex.name,
          sets: Array.from({ length: setsCount }, () => ({ reps: repsInt, weight: 0, completed: false })),
          notes: notesParts.join(" — "),
        };
      });
      const baseName = activeDay.workout_name || `${dayName} Workout`;
      const name = weeks && weeks.length > 1 ? `${baseName} (Week ${weekIdx + 1})` : baseName;
      const res = await axios.post(`${api}/templates`, {
        name,
        category: "Custom",
        equipment: "Gym",
        description: programName ? `From program: ${programName}` : "",
        exercises: tplExercises,
      });
      const newTplId = res.data?.id;

      if (programId && newTplId && activeDay?.day_index != null) {
        try {
          await axios.put(`${api}/programs/${programId}/days/${activeDay.day_index}/link-template`, {
            template_id: newTplId,
          });
          onDayLinked?.(activeDay.day_index, newTplId);
          onProgramsRefresh?.();
        } catch (e) {
          console.warn("Failed to link day to template", e);
        }
      }

      toast.success(`Saved "${name}" to your workouts`);
      onTemplatesRefresh?.();
      onClose?.();
    } catch (e) {
      toast.error("Failed to save as workout");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="bg-[#161616] border-[#27272A] max-w-lg max-h-[85vh] overflow-y-auto" data-testid="day-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold uppercase tracking-tight text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {dayName} · {activeDay.workout_name || (isRest ? "Rest" : "Workout")}
          </DialogTitle>
          {programName && (
            <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">{programName}</p>
          )}
        </DialogHeader>

        {/* Week selector */}
        {weeks && weeks.length > 1 && (
          <div className="mt-2 flex items-center gap-2" data-testid="week-selector">
            <button
              onClick={() => setWeekIdx((i) => Math.max(0, i - 1))}
              disabled={weekIdx === 0}
              className="px-2 py-1 border border-[#27272A] text-[#A1A1AA] hover:border-[#E05A1D] hover:text-[#E05A1D] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid="week-prev-btn"
            >
              <CaretDown size={12} weight="bold" className="rotate-90" />
            </button>
            <div className="flex-1 overflow-x-auto no-scrollbar">
              <div className="flex gap-1">
                {weeks.map((w, i) => (
                  <button
                    key={i}
                    onClick={() => setWeekIdx(i)}
                    className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider transition-all whitespace-nowrap ${
                      weekIdx === i
                        ? "bg-[#E05A1D] text-white"
                        : "bg-[#0F0F0F] border border-[#27272A] text-[#A1A1AA] hover:border-[#3f3f46]"
                    }`}
                    data-testid={`week-tab-${i}`}
                  >
                    {w.label || `Week ${i + 1}`}
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={() => setWeekIdx((i) => Math.min(weeks.length - 1, i + 1))}
              disabled={weekIdx >= weeks.length - 1}
              className="px-2 py-1 border border-[#27272A] text-[#A1A1AA] hover:border-[#E05A1D] hover:text-[#E05A1D] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid="week-next-btn"
            >
              <CaretDown size={12} weight="bold" className="-rotate-90" />
            </button>
          </div>
        )}

        <div className="mt-3 space-y-3">
          {isRest ? (
            <div className="text-center py-12 border border-dashed border-[#27272A]" data-testid="day-detail-rest">
              <Coffee size={36} className="text-[#71717A] mx-auto mb-3" />
              <p className="text-sm font-bold uppercase tracking-wider text-white">Rest Day</p>
              <p className="text-xs text-[#71717A] mt-1">Recovery. Optional light walk or mobility work.</p>
            </div>
          ) : exercises.length === 0 ? (
            <div className="text-center py-10 border border-dashed border-[#27272A]" data-testid="day-detail-empty">
              <Barbell size={32} className="text-[#71717A] mx-auto mb-2" />
              <p className="text-xs text-[#71717A]">No exercises defined for this day yet.</p>
            </div>
          ) : (
            <>
              {/* Day meta */}
              <div className="flex items-center gap-3 text-xs">
                {day.duration_minutes > 0 && (
                  <span className="flex items-center gap-1 text-[#A1A1AA]">
                    <Timer size={12} weight="fill" className="text-[#E05A1D]" />
                    ~{day.duration_minutes} min
                  </span>
                )}
                <span className="flex items-center gap-1 text-[#A1A1AA]">
                  <Barbell size={12} weight="fill" className="text-[#E05A1D]" />
                  {exercises.length} exercise{exercises.length > 1 ? "s" : ""}
                </span>
              </div>

              {/* Exercises */}
              <div className="space-y-2" data-testid="day-detail-exercises">
                {exercises.map((ex, idx) => (
                  <div key={idx} className="bg-[#0F0F0F] border border-[#27272A] p-3 space-y-2" data-testid={`day-detail-exercise-${idx}`}>
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-semibold text-white text-sm">{idx + 1}. {ex.name}</p>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <Stat label="Sets" value={ex.sets > 0 ? ex.sets : "—"} />
                      <Stat label="Reps" value={ex.reps || "—"} />
                      <Stat label="Weight" value={annotateWeightGuidance(ex.weight_guidance, weightUnit, fromKg) || "—"} />
                    </div>
                    {ex.how_to && (
                      <ExerciseHowTo text={ex.how_to} />
                    )}
                    {ex.notes && (
                      <p className="text-[11px] text-[#A1A1AA] italic pt-1 border-t border-[#27272A]">{ex.notes}</p>
                    )}
                  </div>
                ))}
              </div>

              {alreadyLinked ? (
                <div className="w-full mt-1 bg-[#22C55E]/10 border border-[#22C55E]/40 text-[#22C55E] font-bold uppercase tracking-wide py-2.5 text-xs flex items-center justify-center gap-1.5" data-testid="day-linked-badge">
                  <CheckCircle size={14} weight="fill" />
                  Linked to "{linkedTemplate?.name || "your workout"}"
                </div>
              ) : canSaveAsTemplate ? (
                <button
                  onClick={saveAsTemplate}
                  disabled={saving}
                  className="w-full mt-1 bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide py-3 text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                  data-testid="save-day-as-template-btn"
                >
                  {saving ? <><Spinner size={14} className="animate-spin" /> Saving…</> : <><Plus size={14} weight="bold" /> Add to My Workouts</>}
                </button>
              ) : null}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[#161616] border border-[#27272A] py-1.5">
      <p className="text-[9px] font-bold tracking-[0.1em] uppercase text-[#71717A]">{label}</p>
      <p className="text-xs font-bold text-white truncate" title={String(value)}>{value}</p>
    </div>
  );
}

function ProgramWeeksOverview({ program, onDayClick, dense = false, testIdPrefix = "overview" }) {
  // If the program has per-week variations, render each week as its own row.
  // Otherwise render the single weekly pattern (labeled as the only week).
  const weeks = program?.weeks && program.weeks.length > 0
    ? program.weeks
    : [{ week_index: 0, label: "", days: program?.days || [] }];

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-wrapper`}>
      {weeks.map((w, wi) => {
        const weekWorkouts = (w.days || []).filter((d) => !d.rest).length;
        return (
          <div key={wi} className="border border-[#27272A] bg-[#0F0F0F]" data-testid={`${testIdPrefix}-week-${wi}`}>
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-[#27272A] bg-[#161616]">
              <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#E05A1D]">
                {w.label || `Week ${wi + 1}`}
              </p>
              <p className="text-[10px] uppercase tracking-wider text-[#71717A]">{weekWorkouts} workouts</p>
            </div>
            <div className={`grid grid-cols-7 ${dense ? "gap-px" : "gap-1 p-1"}`}>
              {DAY_NAMES.map((dn, idx) => {
                const d = (w.days || []).find((x) => x.day_index === idx);
                const isWorkout = d && !d.rest;
                return (
                  <button
                    key={idx}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (d && onDayClick) onDayClick(d, w.week_index ?? wi);
                    }}
                    disabled={!d || !onDayClick}
                    title={d?.workout_name || ""}
                    className={`${dense ? "py-1" : "py-1.5"} text-center transition-all ${
                      isWorkout
                        ? "bg-[#E05A1D]/15 text-[#E05A1D] hover:bg-[#E05A1D]/35"
                        : d?.rest
                          ? "bg-[#27272A] text-[#71717A] hover:bg-[#3f3f46]"
                          : "text-[#3f3f46] cursor-default"
                    } ${d && onDayClick ? "cursor-pointer" : ""}`}
                    data-testid={`${testIdPrefix}-week-${wi}-day-${idx}`}
                  >
                    <div className="text-[9px] font-bold uppercase tracking-wider">{dn}</div>
                    {d && !dense && (
                      <div className="text-[8px] truncate px-0.5 leading-tight mt-0.5" title={d.workout_name}>
                        {d.rest ? "Rest" : (d.workout_name || "Workout")}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ExerciseHowTo({ text }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="pt-1 border-t border-[#27272A]" data-testid="exercise-how-to">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-[#E05A1D] hover:text-[#F06B2E] transition-colors"
        data-testid="how-to-toggle"
      >
        <span className="flex items-center gap-1.5">
          <Info size={11} weight="fill" /> How to do this
        </span>
        <CaretDown size={11} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <p className="text-[11px] text-[#A1A1AA] mt-1.5 leading-relaxed" data-testid="how-to-text">
          {text}
        </p>
      )}
    </div>
  );
}



/* ───── Phase Timeline (cycle visualization) ───── */
const DEFAULT_PHASE_COLORS = ["#8B5CF6", "#E05A1D", "#EF4444", "#22C55E", "#06B6D4", "#F59E0B"];

function PhaseTimeline({ phases, weeks, testId }) {
  if (!phases?.length || !weeks) return null;
  const totalWeeks = Math.max(weeks, ...phases.map((p) => (p.end_week ?? 0) + 1));

  return (
    <div className="space-y-1.5" data-testid={testId}>
      <p className="text-[9px] uppercase tracking-wider text-[#71717A] font-bold">Periodization</p>
      <div className="flex w-full h-3 overflow-hidden bg-[#0F0F0F] border border-[#27272A]">
        {phases.map((p, i) => {
          const weeksInPhase = Math.max(1, (p.end_week ?? p.start_week) - p.start_week + 1);
          const pct = (weeksInPhase / totalWeeks) * 100;
          const color = p.color || DEFAULT_PHASE_COLORS[i % DEFAULT_PHASE_COLORS.length];
          return (
            <div
              key={i}
              className="h-full"
              style={{ width: `${pct}%`, background: color }}
              title={`${p.name} · weeks ${p.start_week + 1}-${(p.end_week ?? p.start_week) + 1} · ${p.intensity || "—"}`}
            />
          );
        })}
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
        {phases.map((p, i) => {
          const color = p.color || DEFAULT_PHASE_COLORS[i % DEFAULT_PHASE_COLORS.length];
          return (
            <div key={i} className="flex items-center gap-1.5 min-w-0">
              <span className="inline-block w-2 h-2" style={{ background: color }} />
              <span className="text-[10px] font-bold uppercase tracking-wider text-white truncate">
                {p.name}
              </span>
              <span className="text-[10px] text-[#71717A] whitespace-nowrap">
                · w{p.start_week + 1}–{(p.end_week ?? p.start_week) + 1}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
